import { useCallback, useMemo, useSyncExternalStore } from "react";

import { vars } from "@frontend/styles";

import type { MapPalette } from "./MapView.context";
import type { MapTone } from "./MapView.types";
import { MAP_TONES } from "./constants";

const TONE_TOKENS: Record<MapTone, string> = {
  primary: vars.color.primary,
  neutral: vars.color.textMuted,
  success: vars.color.success,
  warning: vars.color.warning,
  error: vars.color.error,
};

const CSS_VAR = /^var\((--[\w-]+)\)$/;

function resolveColor(element: Element, token: string): string {
  const match = CSS_VAR.exec(token.trim());

  if (!match) {
    return token;
  }

  return getComputedStyle(element).getPropertyValue(match[1]).trim() || token;
}

// A theme is a class on <html>, or on a wrapper in the docs previews: any
// attribute change in the tree may recolor the tokens.
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class", "data-theme"],
    subtree: true,
  });

  return () => observer.disconnect();
}

// Google draws polylines itself, so their colors have to be real values: the
// theme tokens are read off the map element as computed styles.
export function useMapPalette(element: HTMLElement | null): MapPalette {
  const getSnapshot = useCallback(
    () =>
      MAP_TONES.map((tone) =>
        element ? resolveColor(element, TONE_TOKENS[tone]) : TONE_TOKENS[tone],
      ).join("|"),
    [element],
  );

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return useMemo(() => {
    const colors = snapshot.split("|");

    return Object.fromEntries(
      MAP_TONES.map((tone, index) => [tone, colors[index]]),
    ) as MapPalette;
  }, [snapshot]);
}
